import { createActionHandler, createReducer, OperationStatus } from '@pressreader/appstore';
import { IFont } from '@pressreader/types';
import { localSettings } from '@pressreader/usersettings';

import { localSettingsCurrentLineSpacingName, localSettingsCurrentScaleName } from '../constants';
import { defaultLineSpacing, type LineSpacing } from '../types/line.spacing';
import type { TopBarNavItemParams } from '../types/topbar.menu';
import { validateLineSpacing } from '../utils/line.spacing';

import {
    addTopBarMenuItemsAction,
    availableFontsChanged,
    changeFontDone,
    fontScaleChanged,
    legacyAppLoadBegan,
    legacyAppLoadDone,
    legacyAppLoadFailed,
    lineSpacingChanged,
    removeTopBarMenuItemsAction,
} from './actions';

interface IState {
    fonts: IFont[];
    currentFontId: string | null;

    currentFontScale: number;
    currentLineSpacing: LineSpacing;
    legacyAppStatus: OperationStatus;

    topBarMenuItems: TopBarNavItemParams[];
}

const initialState: IState = {
    fonts: [],
    currentFontId: null,

    currentFontScale: parseInt(localSettings.get<string>(localSettingsCurrentScaleName, '3'), 10),
    currentLineSpacing: validateLineSpacing(localSettings.get<string>(localSettingsCurrentLineSpacingName, defaultLineSpacing)),
    legacyAppStatus: OperationStatus.None,

    topBarMenuItems: [],
};

const reducer = createReducer(
    initialState,
    createActionHandler(availableFontsChanged, (state, { payload }) => ({
        ...state,
        fonts: payload.fonts,
    })),
    createActionHandler(changeFontDone, (state, { payload }) => ({ ...state, currentFontId: payload.fontId })),
    createActionHandler(fontScaleChanged, (state, { payload }) => ({ ...state, currentFontScale: payload.fontScale })),
    createActionHandler(lineSpacingChanged, (state, { payload }) => ({ ...state, currentLineSpacing: payload.lineSpacing })),
    createActionHandler(legacyAppLoadBegan, state => ({
        ...state,
        legacyAppStatus: OperationStatus.Pending,
    })),
    createActionHandler(legacyAppLoadDone, state => ({
        ...state,
        legacyAppStatus: OperationStatus.Done,
    })),
    createActionHandler(legacyAppLoadFailed, state => ({
        ...state,
        legacyAppStatus: OperationStatus.Failed,
    })),
    createActionHandler(addTopBarMenuItemsAction, (state, { payload }) => {
        const { items } = payload;
        if (!items?.length) {
            return state; // nothing to add
        }

        // Build a map of incoming items for quick replacement
        const incomingByKey = new Map(items.map(i => [i.uniqKey, i] as const));

        // Filter out any existing items that will be replaced
        const preserved = state.topBarMenuItems.filter(i => !incomingByKey.has(i.uniqKey));

        // Append incoming items in the order provided
        const nextItems: TopBarNavItemParams[] = [...preserved, ...items];

        return {
            ...state,
            topBarMenuItems: nextItems,
        };
    }),
    createActionHandler(removeTopBarMenuItemsAction, (state, { payload }) => ({
        ...state,
        topBarMenuItems: state.topBarMenuItems.filter(i => payload.itemUniqKeys.indexOf(i.uniqKey) === -1),
    })),
);

export { IState, reducer };
